/**
 * ImageElementEditor Component
 * Main container for image editing with:
 * - Current image preview or empty state
 * - Select from DAM, Upload new, Remove buttons
 * - Alt text and caption inputs
 * - Link to asset details
 */

import { useState, useCallback } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import {
  Image as ImageIcon,
  FolderOpen,
  Upload,
  Trash2,
  ExternalLink,
  Info,
} from 'lucide-react'
import { DAMAssetPicker } from './DAMAssetPicker'
import { ImageUploader } from './ImageUploader'
import { ImagePreview } from './ImagePreview'
import { AltTextCaptionForm } from './AltTextCaptionForm'
import { assetService } from '@/lib/api/services/assets'
import type { ImageElementContent } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface ImageElementEditorProps {
  value: Partial<ImageElementContent>
  onChange: (content: Partial<ImageElementContent>) => void
  disabled?: boolean
  className?: string
}

interface SelectedAsset {
  id: string
  url: string
  thumbnailUrl?: string
  name: string
  width?: number
  height?: number
  fileSize: number
  mimeType: string
}

// ============================================
// ImageElementEditor Component
// ============================================

export function ImageElementEditor({
  value,
  onChange,
  disabled = false,
  className,
}: ImageElementEditorProps) {
  const [isPickerOpen, setIsPickerOpen] = useState(false)
  const [isUploaderOpen, setIsUploaderOpen] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  // Check if we have an image selected
  const hasImage = !!value.assetId && !!value.assetUrl

  // Handle asset selection from DAM
  const handleAssetSelect = useCallback(
    (asset: SelectedAsset) => {
      onChange({
        ...value,
        assetId: asset.id,
        assetUrl: asset.url,
        thumbnailUrl: asset.thumbnailUrl || asset.url,
        width: asset.width,
        height: asset.height,
        fileSize: asset.fileSize,
        mimeType: asset.mimeType,
        aspectRatio: asset.width && asset.height
          ? `${asset.width}:${asset.height}`
          : undefined,
      })
      setIsPickerOpen(false)
    },
    [value, onChange]
  )

  // Handle upload completion
  const handleUploadComplete = useCallback(
    (asset: SelectedAsset) => {
      onChange({
        ...value,
        assetId: asset.id,
        assetUrl: asset.url,
        thumbnailUrl: asset.thumbnailUrl || asset.url,
        width: asset.width,
        height: asset.height,
        fileSize: asset.fileSize,
        mimeType: asset.mimeType,
        aspectRatio: asset.width && asset.height
          ? `${asset.width}:${asset.height}`
          : undefined,
      })
      setIsUploaderOpen(false)
    },
    [value, onChange]
  )

  // Handle remove image
  const handleRemove = useCallback(() => {
    onChange({
      altText: value.altText,
      caption: value.caption,
    })
  }, [value.altText, value.caption, onChange])

  // Handle alt text change
  const handleAltTextChange = useCallback(
    (altText: string) => {
      onChange({ ...value, altText })
    },
    [value, onChange]
  )

  // Handle caption change
  const handleCaptionChange = useCallback(
    (caption: string) => {
      onChange({ ...value, caption })
    },
    [value, onChange]
  )

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    return assetService.formatFileSize(bytes)
  }

  return (
    <div className={cn('space-y-6', className)}>
      {/* Image Selection Section */}
      <div className="space-y-4">
        {/* Current Image Preview or Empty State */}
        {hasImage ? (
          <div className="space-y-3">
            {/* Image Preview Card */}
            <div
              className={cn(
                'relative rounded-xl overflow-hidden border border-neutral-200 dark:border-neutral-700',
                'bg-neutral-50 dark:bg-neutral-800/50',
                !disabled && 'cursor-pointer hover:border-neutral-300 dark:hover:border-neutral-600 transition-colors'
              )}
              onClick={() => !disabled && setIsPreviewOpen(true)}
            >
              <div className="aspect-video relative">
                <img
                  src={value.thumbnailUrl || value.assetUrl}
                  alt={value.altText || 'Selected image'}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    const target = e.currentTarget
                    target.style.display = 'none'
                    const placeholder = target.nextElementSibling as HTMLElement
                    if (placeholder) placeholder.style.display = 'flex'
                  }}
                />
                <div
                  className="absolute inset-0 hidden items-center justify-center bg-neutral-100 dark:bg-neutral-800"
                >
                  <ImageIcon className="h-16 w-16 text-neutral-300 dark:text-neutral-600" />
                </div>

                {/* Zoom Overlay */}
                {!disabled && (
                  <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
                    <span className="bg-white/90 dark:bg-neutral-900/90 text-neutral-700 dark:text-neutral-300 px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg">
                      Click to zoom
                    </span>
                  </div>
                )}
              </div>

              {/* Image Info Bar */}
              <div className="px-4 py-3 bg-white dark:bg-neutral-900 border-t border-neutral-200 dark:border-neutral-700">
                <div className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-4 text-neutral-500 dark:text-neutral-400">
                    {value.width && value.height && (
                      <span>{value.width} x {value.height}px</span>
                    )}
                    {value.fileSize && (
                      <span>{formatFileSize(value.fileSize)}</span>
                    )}
                    {value.mimeType && (
                      <span className="uppercase">{value.mimeType.split('/')[1]}</span>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 text-neutral-500 hover:text-blue-600"
                    onClick={(e) => {
                      e.stopPropagation()
                      window.open(`/assets?assetId=${value.assetId}`, '_blank')
                    }}
                    disabled={disabled}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    View in DAM
                  </Button>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsPickerOpen(true)}
                disabled={disabled}
                className="rounded-lg"
              >
                <FolderOpen className="h-4 w-4 mr-1.5" />
                Change Image
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setIsUploaderOpen(true)}
                disabled={disabled}
                className="rounded-lg"
              >
                <Upload className="h-4 w-4 mr-1.5" />
                Upload New
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemove}
                disabled={disabled}
                className="rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div
            className={cn(
              'p-8 rounded-xl border-2 border-dashed',
              'border-neutral-200 dark:border-neutral-700',
              'bg-neutral-50 dark:bg-neutral-800/30',
              'text-center'
            )}
          >
            <div className="inline-flex p-4 rounded-full bg-purple-100 dark:bg-purple-900/30 mb-4">
              <ImageIcon className="h-8 w-8 text-purple-500 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">
              No image selected
            </h3>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-6 max-w-sm mx-auto">
              Select an image from your asset library or upload a new one
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                type="button"
                variant="default"
                onClick={() => setIsPickerOpen(true)}
                disabled={disabled}
                className="rounded-xl"
              >
                <FolderOpen className="h-4 w-4 mr-2" />
                Browse Library
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsUploaderOpen(true)}
                disabled={disabled}
                className="rounded-xl"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Image
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Alt Text and Caption Form */}
      <Separator />
      <AltTextCaptionForm
        altText={value.altText || ''}
        caption={value.caption || ''}
        onAltTextChange={handleAltTextChange}
        onCaptionChange={handleCaptionChange}
        disabled={disabled}
        showWarning={hasImage && !value.altText}
      />

      {/* Accessibility Info */}
      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
        <p className="text-xs text-blue-700 dark:text-blue-300 flex items-start gap-2">
          <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>
            Alt text describes the image for screen readers and when the image fails to load.
            Keep it concise but descriptive. Captions are optional and display below the image.
          </span>
        </p>
      </div>

      {/* DAM Asset Picker Modal */}
      <DAMAssetPicker
        open={isPickerOpen}
        onOpenChange={setIsPickerOpen}
        onSelect={handleAssetSelect}
      />

      {/* Image Uploader Modal */}
      <ImageUploader
        open={isUploaderOpen}
        onOpenChange={setIsUploaderOpen}
        onUploadComplete={handleUploadComplete}
      />

      {/* Image Preview Modal */}
      <ImagePreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        imageUrl={value.assetUrl || ''}
        altText={value.altText}
        width={value.width}
        height={value.height}
        fileSize={value.fileSize}
        mimeType={value.mimeType}
      />
    </div>
  )
}

export default ImageElementEditor
